<script setup lang="ts">
import { DeleteButton, EditButton } from '@/components/Buttons'
import { StackLayout } from '@/components/Layouts'
import ConfirmationDialog from '@/components/Modals/Dialog/ConfirmationDialog.vue'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import HrefWrapper from '@/components/Wrappers/HrefWrapper.vue'
import { EConfirmDialog } from '@/types/enum/ConfirmDialogEnum'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import { userHasPermission } from '@/utils/permissionsUtils'
import { computed, getCurrentInstance, ref } from 'vue'

type Props = {
    editIcon?: string
    deleteIcon?: string
    titleItemKey?: string
    type?: EConfirmDialog.ARCHIVE | EConfirmDialog.DELETE
    hideEdit?: boolean
    hideDelete?: boolean
    editPermission?: PermissionsEnums
    hasEditPermission?: boolean
    deletePermission?: PermissionsEnums
    hasDeletePermission?: boolean
    disabled?: boolean
    withoutDeleteDialog?: boolean

    row: Record<string, unknown>

    editButtonHref?: string
    hrefKey?: string
}
const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'goTo', data: any): void
    (e: 'delete', data: any): void
}>()

const selectedRow = ref()

const handleDeleteButton = (data: any, e: MouseEvent) => {
    e.stopPropagation()
    if (props.withoutDeleteDialog) return emit('delete', data)
    selectedRow.value = data
}

const confirmDeleteRow = () => {
    emit('delete', selectedRow.value)
    selectedRow.value = undefined
}

const deleteIsDefined = computed(() => getCurrentInstance()?.vnode.props?.['onDelete'])
</script>

<template>
    <StackLayout :gap="8" class="button-end">
        <slot name="before" v-bind="row" />
        <PermissionWrapper
            v-if="!hideEdit"
            :permission="editPermission"
            :hasPermission="hasEditPermission">
            <HrefWrapper
                :hasPermission="editPermission ? userHasPermission(editPermission) : true"
                :href="`${editButtonHref}/${row[hrefKey ?? 'id']}`">
                <EditButton :icon="editIcon" isResponsive />
            </HrefWrapper>
        </PermissionWrapper>
        <PermissionWrapper
            v-if="!hideDelete"
            :permission="deletePermission"
            :hasPermission="hasDeletePermission">
            <DeleteButton
                v-if="deleteIsDefined"
                :icon="deleteIcon"
                :type="type"
                @click="e => handleDeleteButton(row, e)"
                isResponsive />
        </PermissionWrapper>
        <slot name="after" v-bind="row" />
    </StackLayout>
    <ConfirmationDialog
        v-if="selectedRow"
        :open="!!selectedRow"
        :type="type ?? EConfirmDialog.DELETE"
        :titleItem="selectedRow[titleItemKey ?? 'name']"
        :disabled="disabled"
        @close="selectedRow = undefined"
        @confirm="confirmDeleteRow">
        <slot name="confirmationDialog" />
    </ConfirmationDialog>
</template>
